import { DatePipe } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { Subscription, filter, finalize } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  dynamicFormSubmissionsTopic,
  liveStatusTranslationKey
} from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { DynamicApiService, DynamicFormDetail, DynamicFormPublication, DynamicFormSubmission } from './dynamic-api.service';
import { DynamicFormBuilderComponent } from './form-builder/dynamic-form-builder.component';
import { FormVersionDiffComponent } from './form-builder/form-version-diff.component';
import { DynamicFormSharePanelComponent } from './dynamic-form-share-panel.component';
import { buildPublicFormUrl } from '../public-forms/public-form-url.util';

@Component({
  standalone: true,
  selector: 'ain-dynamic-form-detail',
  imports: [
    DatePipe,
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    DynamicFormBuilderComponent,
    FormVersionDiffComponent,
    DynamicFormSharePanelComponent,
    ButtonModule,
    InputTextModule,
    TagModule,
    TableModule,
    FormsModule,
    RouterLink,
    ConfirmDialogModule
  ],
  providers: [MessageService, ConfirmationService],
  template: `
    <ain-page [title]="form()?.name ?? ''" [description]="t('dynamic.forms.detail')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'dynamic.form.help.title'"
        [descriptionKey]="'dynamic.form.help.description'"
        [bulletKeys]="['dynamic.form.help.bullets.versioning', 'dynamic.form.help.bullets.publication']" />

      <a routerLink="/dynamic/forms">{{ t('common.back') }}</a>

      @if (pageLoading()) {
        <ain-state type="loading" [title]="t('dynamic.form.loadingSchema')" />
      } @else if (pageError()) {
        <ain-state type="error" [title]="t('dynamic.form.schemaLoadError')" [message]="pageError() ?? ''" />
      } @else if (form(); as f) {
        <section class="metadata card">
          <div class="actions">
            <button pButton severity="secondary" [label]="t('common.save')" (click)="saveMetadata()"></button>
            @if (canArchiveForm()) {
              <button pButton severity="warn" [label]="t('dynamic.form.archive.action')" (click)="confirmArchiveForm()"></button>
            }
            @if (canDeleteForm()) {
              <button pButton severity="danger" [label]="t('dynamic.form.delete.action')" (click)="confirmDeleteForm()"></button>
            }
          </div>

          <p>{{ t('columns.status') }}: {{ f.status }} | v{{ runtimeVersion() }}</p>

          <label>{{ t('columns.name') }}</label>
          <input pInputText [(ngModel)]="name" class="w-full" />
          <label>{{ t('columns.description') }}</label>
          <input pInputText [(ngModel)]="description" class="w-full" />
        </section>

        <ain-dynamic-form-builder
          [formId]="f.id"
          (saved)="reloadRuntimeVersion()"
          (published)="reloadRuntimeVersion()"
          (publicationSlugSuggestion)="applyPublicationSlug($event)" />

        <section class="versions card">
          <h3>{{ t('dynamic.versions') }}</h3>
          <ain-form-version-diff [formId]="f.id" [versions]="f.versions" />
        </section>

        <section class="publication card">
          <h3>{{ t('dynamic.publication') }}</h3>
          <ain-info-hint
            [titleKey]="'dynamic.builder.publish.publicationHintTitle'"
            [descriptionKey]="'dynamic.builder.publish.publicationHintDescription'"
            [bulletKeys]="['dynamic.builder.publish.publicationHintBullet']" />
          <input pInputText [(ngModel)]="slug" [placeholder]="t('dynamic.builder.publish.slug')" />
          <button pButton class="mt" [label]="t('dynamic.createPublication')" (click)="createPublication()"></button>
          @if (publicUrl()) {
            <p>{{ t('dynamic.publicUrl') }}: <a [href]="publicUrl()" target="_blank" rel="noopener">{{ publicUrl() }}</a></p>
            <ain-dynamic-form-share-panel [slug]="slug" />
          }
        </section>

        <h3>{{ t('dynamic.publications') }}</h3>
        <p-table [value]="publications()" [paginator]="true" [rows]="5">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('columns.name') }}</th>
              <th>{{ t('columns.status') }}</th>
              <th>{{ t('columns.description') }}</th>
              <th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.slug }}</td>
              <td>{{ row.isPublished }}</td>
              <td>{{ row.visibility }}</td>
              <td>
                @if (row.isPublished && canUnpublishPublication()) {
                  <button
                    pButton
                    type="button"
                    severity="secondary"
                    [text]="true"
                    [label]="t('dynamic.form.unpublish.action')"
                    (click)="confirmUnpublishPublication(row.id)"></button>
                }
              </td>
            </tr>
          </ng-template>
        </p-table>
        <p-confirmDialog />

        <h3>{{ t('dynamic.submissions') }}</h3>
        <div class="submissions-toolbar">
          <span class="live-status" [class]="liveUpdateService.connectionState()">
            {{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}
          </span>
        </div>
        <p-table [value]="submissions()" [paginator]="true" [rows]="10">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('columns.status') }}</th>
              <th>{{ t('columns.date') }}</th>
              <th>{{ t('dynamic.submissions.detail.recordColumn') }}</th>
              <th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr class="clickable-row" (click)="openSubmission(row.id)">
              <td>
                <p-tag [value]="submissionStatusLabel(row.status)" [severity]="submissionStatusSeverity(row.status)" />
              </td>
              <td>{{ row.submittedAtUtc | date:'medium' }}</td>
              <td>
                @if (row.recordId) {
                  <p-tag [value]="t('dynamic.submissions.detail.hasRecord')" severity="success" />
                } @else {
                  <span>—</span>
                }
              </td>
              <td>
                <button
                  pButton
                  type="button"
                  severity="secondary"
                  [text]="true"
                  [label]="t('common.open')"
                  (click)="openSubmission(row.id); $event.stopPropagation()"></button>
              </td>
            </tr>
          </ng-template>
        </p-table>
      }
    </ain-page>
  `,
  styles: [`
    .actions { display: flex; gap: 8px; margin: 12px 0; }
    .mt { margin-top: 8px; }
    .w-full { width: 100%; margin-bottom: 8px; }
    .clickable-row { cursor: pointer; }
    .clickable-row:hover { background: #f8fafc; }
    .submissions-toolbar { display: flex; justify-content: flex-end; margin-bottom: 8px; }
    .live-status {
      font-size: 0.85rem;
      font-weight: 600;
      padding: 4px 10px;
      border-radius: 999px;
      background: #f3f4f6;
      color: #6b7280;
    }
    .live-status.connected { background: #dcfce7; color: #166534; }
    .live-status.connecting, .live-status.reconnecting { background: #fef9c3; color: #854d0e; }
    .live-status.disconnected { background: #fee2e2; color: #991b1b; }
    .card {
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      padding: 16px;
      margin: 16px 0;
      display: grid;
      gap: 12px;
    }
  `]
})
export class DynamicFormDetailComponent implements OnInit, OnDestroy {
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusTranslationKey = liveStatusTranslationKey;
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(DynamicApiService);
  private readonly localization = inject(LocalizationService);
  private readonly messages = inject(MessageService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private liveEventsSubscription: Subscription | null = null;
  private subscribedTopic = '';

  readonly form = signal<DynamicFormDetail | null>(null);
  readonly publications = signal<DynamicFormPublication[]>([]);
  readonly submissions = signal<DynamicFormSubmission[]>([]);
  readonly pageLoading = signal(true);
  readonly pageError = signal<string | null>(null);
  readonly runtimeVersion = signal(0);
  slug = '';
  name = '';
  description = '';
  readonly publicUrl = signal('');

  ngOnInit(): void {
    this.liveEventsSubscription = this.liveUpdateService.events$
      .pipe(filter(event =>
        event.eventType === LiveUpdateEventTypes.dynamicFormSubmissionCompleted
        || event.eventType === LiveUpdateEventTypes.dynamicFormSubmissionReceived))
      .subscribe(() => this.loadSubmissions(false));

    void this.setupLiveSubscription();
    this.loadPage();
  }

  ngOnDestroy(): void {
    this.liveEventsSubscription?.unsubscribe();
    void this.unsubscribeFromTopic();
  }

  saveMetadata(): void {
    const form = this.form();
    if (!form) return;

    this.api.updateForm(form.id, this.name, this.description, form.targetEntityId).subscribe({
      next: () => {
        this.messages.add({ severity: 'success', summary: this.t('common.saved') });
        this.loadPage();
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  applyPublicationSlug(slug: string): void {
    if (!this.slug.trim() && slug.trim()) {
      this.slug = slug;
    }
  }

  createPublication(): void {
    const id = this.form()?.id;
    if (!id || !this.slug) return;

    const activePublication = this.publications().find(item => item.isPublished);
    if (activePublication) {
      this.setActivePublication(activePublication.slug);
      return;
    }

    this.api.createPublication(id, this.slug).subscribe({
      next: publication => {
        this.setActivePublication(publication.slug);
        this.messages.add({ severity: 'success', summary: this.t('common.saved') });
        this.loadPage();
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  reloadRuntimeVersion(): void {
    const id = this.form()?.id;
    if (!id) {
      return;
    }
    this.api.getFormRuntime(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: runtime => this.runtimeVersion.set(runtime.version)
      });
  }

  canArchiveForm(): boolean {
    const form = this.form();
    return !!form && form.status !== 'Archived' && this.hasFormAction('Archive');
  }

  canDeleteForm(): boolean {
    return !!this.form() && this.hasFormAction('Delete');
  }

  canUnpublishPublication(): boolean {
    return this.hasFormAction('Publish');
  }

  confirmArchiveForm(): void {
    this.confirmation.confirm({
      header: this.t('dynamic.form.archive.confirmTitle'),
      message: this.t('dynamic.form.archive.confirmMessage'),
      accept: () => this.archiveForm()
    });
  }

  confirmDeleteForm(): void {
    this.confirmation.confirm({
      header: this.t('dynamic.form.delete.confirmTitle'),
      message: this.t('dynamic.form.delete.confirmMessage'),
      accept: () => this.deleteForm()
    });
  }

  confirmUnpublishPublication(publicationId: string): void {
    this.confirmation.confirm({
      header: this.t('dynamic.form.unpublish.confirmTitle'),
      message: this.t('dynamic.form.unpublish.confirmMessage'),
      accept: () => this.unpublishPublication(publicationId)
    });
  }

  private archiveForm(): void {
    const form = this.form();
    if (!form) {
      return;
    }

    this.api.archiveForm(form.id).subscribe({
      next: () => {
        this.messages.add({ severity: 'success', summary: this.t('dynamic.form.archive.success') });
        this.loadPage();
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('dynamic.form.archive.failed') })
    });
  }

  private deleteForm(): void {
    const form = this.form();
    if (!form) {
      return;
    }

    this.api.deleteForm(form.id).subscribe({
      next: () => {
        this.messages.add({ severity: 'success', summary: this.t('dynamic.form.delete.success') });
        void this.router.navigate(['/dynamic/forms']);
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('dynamic.form.delete.failed') })
    });
  }

  private unpublishPublication(publicationId: string): void {
    this.api.unpublishPublication(publicationId).subscribe({
      next: () => {
        this.messages.add({ severity: 'success', summary: this.t('dynamic.form.unpublish.success') });
        this.loadPage();
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('dynamic.form.unpublish.failed') })
    });
  }

  private hasFormAction(action: string): boolean {
    const context = this.contextService.context();
    const roles = new Set(context?.roles ?? []);
    if (roles.has('SystemAdmin')) {
      return true;
    }

    const node = (context?.permissions ?? []).find(item => item.key === 'dynamic.forms');
    if (!node) {
      return false;
    }

    const actions = new Set((node.actions ?? []).map(item => item.toLowerCase()));
    return actions.has(action.toLowerCase());
  }

  t(key: string): string { return this.localization.translate(key); }

  openSubmission(submissionId: string): void {
    const formId = this.form()?.id ?? this.route.snapshot.paramMap.get('id');
    if (!formId) {
      return;
    }
    void this.router.navigate(['/dynamic/forms', formId, 'submissions', submissionId]);
  }

  submissionStatusLabel(status: string): string {
    const key = `dynamic.submissions.status.${status.toLowerCase()}`;
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  submissionStatusSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' | undefined {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'processing':
      case 'validated':
        return 'info';
      case 'received':
        return 'secondary';
      case 'failed':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  private loadPage(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.pageLoading.set(false);
      this.pageError.set(this.t('dynamic.form.schemaLoadError'));
      return;
    }

    this.pageLoading.set(true);
    this.pageError.set(null);

    this.api.getForm(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: f => {
        this.form.set(f);
        this.name = f.name;
        this.description = f.description ?? '';
      },
      error: () => {
        this.pageError.set(this.t('dynamic.form.schemaLoadError'));
        this.pageLoading.set(false);
      }
    });

    this.api.getFormRuntime(id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.pageLoading.set(false))
      )
      .subscribe({
        next: runtime => {
          this.runtimeVersion.set(runtime.version);
          if (runtime.activePublicationSlug) {
            this.setActivePublication(runtime.activePublicationSlug);
          }
        },
        error: () => this.pageError.set(this.t('dynamic.form.schemaLoadError'))
      });

    this.api.listPublications().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: list => {
        const items = list.filter(item => item.formDefinitionId === id);
        this.publications.set(items);
        const published = items.find(item => item.isPublished);
        if (published?.slug) {
          this.setActivePublication(published.slug);
        } else {
          this.slug = '';
          this.publicUrl.set('');
        }
      }
    });

    this.loadSubmissions();
  }

  private loadSubmissions(showLoading = true): void {
    const id = this.form()?.id ?? this.route.snapshot.paramMap.get('id');
    if (!id) return;

    if (showLoading) {
      this.api.listSubmissions(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: list => this.submissions.set(list)
      });
      return;
    }

    this.api.listSubmissions(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: list => this.submissions.set(list)
    });
  }

  private setActivePublication(slug: string): void {
    this.slug = slug;
    this.publicUrl.set(buildPublicFormUrl(slug));
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.contextService.context()?.tenantId;
    if (!tenantId) return;

    this.subscribedTopic = dynamicFormSubmissionsTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }

  private async unsubscribeFromTopic(): Promise<void> {
    if (!this.subscribedTopic) return;
    await this.liveUpdateService.unsubscribe(this.subscribedTopic);
    this.subscribedTopic = '';
  }
}
